/**
 * Analytics Event Types
 *
 * This file defines all analytics events tracked in DramaDetector.
 *
 * Benefits:
 * - IDE autocomplete for event names and properties
 * - Compile-time type checking (catch typos before runtime)
 * - Self-documenting event catalog
 * - Zero runtime overhead (types erased at compile)
 *
 * Usage:
 *   trackEvent('Analysis_Submit', {
 *     text_length: 450,
 *     analysis_latency_ms: 1240,
 *     ...
 *   });
 *
 * Related: See TRACKING.md for naming conventions and process
 */

import { analytics } from '../../analytics-wrapper';

// Re-export shared types
export * from './types';

// Re-export event properties from domain files
export type { AnalysisEventProperties } from './analysis';
export type { TrialEventProperties } from './trial';
export type { SubscriptionEventProperties } from './subscription';
export type { UsageEventProperties } from './usage';
export type { SocialEventProperties } from './social';
export type { NavigationEventProperties } from './navigation';
export type { ResultsEventProperties } from './results';
export type { PaywallEventProperties } from './paywall';
export type { RevisionEventProperties } from './revision';
export type { SafetyEventProperties } from './safety';
export type { TheatricalModeEventProperties } from './theatrical-mode';
export type { PersistenceEventProperties } from './persistence';
export type { NotificationEventProperties } from './notifications';
export type { ManifestEventProperties } from './manifest';
export type { ReferralEventProperties } from './referral';
export type { AuthEventProperties } from './auth';

// Import event properties
import type { AnalysisEventProperties } from './analysis';
import type { TrialEventProperties } from './trial';
import type { SubscriptionEventProperties } from './subscription';
import type { UsageEventProperties } from './usage';
import type { SocialEventProperties } from './social';
import type { NavigationEventProperties } from './navigation';
import type { ResultsEventProperties } from './results';
import type { PaywallEventProperties } from './paywall';
import type { RevisionEventProperties } from './revision';
import type { SafetyEventProperties } from './safety';
import type { TheatricalModeEventProperties } from './theatrical-mode';
import type { PersistenceEventProperties } from './persistence';
import type { NotificationEventProperties } from './notifications';
import type { ManifestEventProperties } from './manifest';
import type { ReferralEventProperties } from './referral';
import type { AuthEventProperties } from './auth';

/**
 * Event properties type map
 *
 * Combines all event properties from domain-specific files.
 */
export type EventProperties = AnalysisEventProperties &
  TrialEventProperties &
  SubscriptionEventProperties &
  UsageEventProperties &
  SocialEventProperties &
  NavigationEventProperties &
  ResultsEventProperties &
  PaywallEventProperties &
  RevisionEventProperties &
  SafetyEventProperties &
  TheatricalModeEventProperties &
  PersistenceEventProperties &
  NotificationEventProperties &
  ManifestEventProperties &
  ReferralEventProperties &
  AuthEventProperties;

/**
 * Type-safe event tracking wrapper
 *
 * Usage:
 *   trackEvent('Analysis_Submit', {
 *     text_length: 450,
 *     analysis_latency_ms: 1240,
 *     suggestion_count: 5,
 *     improvement_areas: ['tone', 'clarity'],
 *     ai_model: 'llama-3.3-70b',
 *   });
 *
 * Benefits:
 * - Autocomplete for event names (IDE suggests all events)
 * - Autocomplete for properties (IDE suggests required fields)
 * - Compile-time errors for typos or missing properties
 * - No runtime overhead (types erased at compile)
 */
export function trackEvent<TEvent extends keyof EventProperties>(
  eventName: TEvent,
  properties: EventProperties[TEvent]
): void {
  analytics.capture(eventName, properties);
}

/**
 * Event name type (for use in type annotations)
 *
 * Usage:
 *   const eventName: EventName = 'Analysis_Submit';
 */
export type EventName = keyof EventProperties;
